import { useState, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import Navbar from '../Navbar';
import BURequisitionApprovals from './BURequisitionApprovals';
import BUExitApprovals from './BUExitApprovals';
import DataImport from './DataImport';
import EmptyState from '../common/EmptyState';
import { DashboardOverviewSkeleton } from '../common/Skeleton';
import { API_BASE } from '../../utils/config';

// Helper API caller
const apiCall = async (url, options = {}) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const tenant = (parts.length >= 3) ? parts[0] : (parts.length === 2 && parts[1] === 'localhost') ? parts[0] : '';
  const headers = {
    'Content-Type': 'application/json',
    'x-user-id': user.id || '',
    'x-user-role': user.user_type || user.role || 'BU_HEAD',
    'X-Tenant-ID': tenant,
    ...options.headers
  };
  const baseUrl = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
  const res = await fetch(`${baseUrl}${url}`, { ...options, headers });
  if (!res.ok) throw new Error(`HTTP error ${res.status}`);
  return res.json();
};

export default function BUDashboard() {
  const { subTab } = useParams();
  const navigate = useNavigate();

  const urlToStateMap = {
    staff: 'employees',
    exits: 'leavers',
    hiring: 'requisitions',
    interviews: 'interviews',
    placements: 'positions',
    import: 'onboard'
  };

  const stateToUrlMap = {
    dashboard: '',
    employees: 'staff',
    leavers: 'exits',
    requisitions: 'hiring',
    interviews: 'interviews',
    positions: 'placements',
    onboard: 'import'
  };

  const [activeTab, setActiveTab] = useState(urlToStateMap[subTab] || 'dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    projectHeadcounts: [],
    scheduledInterviewsCount: 0,
    filledPositionsCount: 0,
    totalEmployeeCount: 0,
    leaverEmployees: [],
    activeEmployees: [],
    scheduledInterviewsList: [],
    filledPositionsList: [],
    requisitionStats: { DRAFT: 0, BU_APPROVED: 0, PUBLISHED: 0, FILLED: 0, REJECTED: 0 }
  });

  const [selectedManager, setSelectedManager] = useState(null);

  const buUser = JSON.parse(localStorage.getItem('user') || '{}');
  const location = useLocation();

  const fetchBUMetrics = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/bu/metrics');
      setMetrics(prev => ({
        ...prev,
        ...data
      }));
    } catch (err) {
      console.error('Failed to load BU metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  const [notifyingId, setNotifyingId] = useState(null);

  const handleNotify = async (id) => {
    try {
      setNotifyingId(id);
      const res = await apiCall(`/api/employees/exits/${id}/notify`, {
        method: 'POST'
      });
      alert(res.message || 'Notification sent to Manager and HR successfully!');
    } catch (err) {
      console.error('Failed to send exit notification:', err);
      alert('Failed to send exit notification.');
    } finally {
      setNotifyingId(null);
    }
  };

  const handleTabChange = (newTab) => {
    setSearchQuery(''); // Reset search query when tabs change
    const urlParam = stateToUrlMap[newTab];
    if (urlParam) {
      navigate(`/bu-dashboard/${urlParam}`);
    } else {
      navigate('/bu-dashboard');
    }
  };

  const exportRosterToCSV = () => {
    if (metrics.activeEmployees.length === 0) {
      alert("No active employees found to export!");
      return;
    }

    const headers = ['Employee Name', 'Email Address', 'Phone Number', 'Project Assignment', 'Interviewer Level', 'Joining Date'];
    const rows = metrics.activeEmployees.map(emp => [
      emp.name || '',
      emp.email || '',
      emp.phone || '',
      emp.project_name || 'General Pool',
      emp.interviewer_level || 'NONE',
      emp.joining_date ? new Date(emp.joining_date).toLocaleDateString() : 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(val => `"${val.replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `bu_staff_roster_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    fetchBUMetrics();
    
    // Clean up long auth_data query parameter from the URL bar
    if (window.location.search.includes('auth_data')) {
      const cleanSearch = window.location.search.replace(/[?&]auth_data=[^&]+/, '').replace(/^&/, '?').replace(/\?$/, '');
      const newUrl = window.location.pathname + cleanSearch;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  useEffect(() => {
    const targetTab = urlToStateMap[subTab] || 'dashboard';
    if (activeTab !== targetTab) {
      setActiveTab(targetTab);
    }
  }, [subTab]);

  useEffect(() => {
    if (location.state?.activeTab) {
      handleTabChange(location.state.activeTab);
    }
  }, [location.state]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSelectedManager(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const tabs = [
    {
      id: 'employees',
      label: 'Employees',
      icon: (
        <svg style={styles.tabIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    },
    {
      id: 'leavers',
      label: 'Leavers',
      icon: (
        <svg style={styles.tabIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    },
    {
      id: 'requisitions',
      label: 'Projects Wise Requirement',
      icon: (
        <svg style={styles.tabIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      id: 'interviews',
      label: 'Interview Scheduled',
      icon: (
        <svg style={styles.tabIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      id: 'positions',
      label: 'Positions Filled',
      icon: (
        <svg style={styles.tabIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
        </svg>
      )
    }
  ];

  const renderContent = () => {
    if (loading) {
      return <DashboardOverviewSkeleton statsCount={4} showGrid={true} />;
    }

    // Safe fallbacks to prevent runtime crashes if keys are missing from API payload
    metrics.projectHeadcounts = metrics.projectHeadcounts || [];
    metrics.activeEmployees = metrics.activeEmployees || [];
    metrics.leaverEmployees = metrics.leaverEmployees || [];
    metrics.scheduledInterviewsList = metrics.scheduledInterviewsList || [];
    metrics.filledPositionsList = metrics.filledPositionsList || [];

    switch (activeTab) {
      case 'dashboard':
        return (
          <div style={styles.dashboardView}>
            {/* Project Allocation & Headcount Share */}
            <div>
              <div style={styles.sectionHeader}>
                <h3 style={styles.tableTitle}>Project Allocation & Headcount Share</h3>
                <p style={styles.sectionSubtitle}>Headcount distributions and operational status per active project.</p>
              </div>
              {metrics.projectHeadcounts.length === 0 ? (
                <div style={styles.empty}>No projects found.</div>
              ) : (
                <div style={styles.projectGrid}>
                  {(() => {
                    const totalEmp = metrics.projectHeadcounts.reduce((sum, p) => sum + p.employee_count, 0);
                    return metrics.projectHeadcounts.map((proj, i) => {
                      const percentage = totalEmp > 0 ? ((proj.employee_count / totalEmp) * 100).toFixed(1) : 0;
                      // Curate different gradient styles for various projects
                      const gradients = [
                        'linear-gradient(135deg, #4f46e5 0%, #8b5cf6 100%)',
                        'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                        'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
                        'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                      ];
                      const gradient = gradients[i % gradients.length];
                      return (
                        <div style={styles.projectCard} key={proj.project_id}>
                          <div style={styles.projectCardHeader}>
                            <span style={styles.projectBadge}>ACTIVE</span>
                            <span style={styles.projectShareBadge}>{percentage}% Share</span>
                          </div>
                          <h4 style={styles.projectCardName}>{proj.project_name}</h4>

                          {/* Manager Pill with Click Details */}
                          <div 
                            style={styles.managerCardPill}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedManager(proj);
                            }}
                            role="button"
                            tabIndex={0}
                            title="Click to view manager contact & project details"
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                              <div style={styles.managerAvatarChip}>
                                {proj.manager_name ? proj.manager_name.trim().charAt(0).toUpperCase() : '?'}
                              </div>
                              <div style={styles.managerInfoCol}>
                                <span style={styles.managerLabelText}>Manager</span>
                                <span style={styles.managerNameText}>{proj.manager_name ? proj.manager_name.trim() : 'Unassigned'}</span>
                              </div>
                            </div>
                            <span style={styles.managerViewBtn}>Details →</span>
                          </div>

                          <div style={styles.projectCardMetrics}>
                            <svg style={styles.projectCardIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                            <span style={styles.projectHeadcountText}>{proj.employee_count} Active Members</span>
                          </div>
                          <div style={styles.progressContainer}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={styles.progressLabel}>Headcount Allocation</span>
                              <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted, #64748b)' }}>{percentage}%</span>
                            </div>
                            <div style={styles.progressBarBg}>
                              <div style={{ ...styles.progressBarFill, width: `${percentage}%`, background: gradient }} />
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>

            {/* Visual Requisition Analytics */}
            <div style={styles.analyticsCard}>
              <div style={styles.analyticsCardHeaderBlock}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <h4 style={styles.analyticsTitle}>Requisition Pipeline Analytics</h4>
                    <p style={styles.analyticsSubtitle}>Workflow status distribution across all active hiring requisitions.</p>
                  </div>
                  {metrics.requisitionStats?.DRAFT > 0 && (
                    <button
                      style={styles.pendingReviewPillBtn}
                      onClick={() => handleTabChange('requisitions')}
                    >
                      ⚡ Review Pending Requisitions ({metrics.requisitionStats.DRAFT}) →
                    </button>
                  )}
                </div>
              </div>
              {(() => {
                const stats = metrics.requisitionStats || { DRAFT: 0, BU_APPROVED: 0, PUBLISHED: 0, FILLED: 0, REJECTED: 0 };
                const totalReqs = (stats.DRAFT || 0) + (stats.BU_APPROVED || 0) + (stats.PUBLISHED || 0) + (stats.FILLED || 0) + (stats.REJECTED || 0);
                
                const statusItems = [
                  { key: 'DRAFT', label: 'Pending BU Approval', val: stats.DRAFT || 0, color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
                  { key: 'BU_APPROVED', label: 'Approved (Pending HR)', val: stats.BU_APPROVED || 0, color: '#4f46e5', bg: 'rgba(79, 70, 229, 0.1)' },
                  { key: 'PUBLISHED', label: 'Active Screening', val: stats.PUBLISHED || 0, color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' },
                  { key: 'FILLED', label: 'Filled Positions', val: stats.FILLED || 0, color: '#06b6d4', bg: 'rgba(6, 182, 212, 0.1)' },
                  { key: 'REJECTED', label: 'Rejected', val: stats.REJECTED || 0, color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' }
                ];

                return (
                  <div style={styles.analyticsGrid}>
                    {statusItems.map((item) => {
                      const percent = totalReqs > 0 ? ((item.val / totalReqs) * 100).toFixed(0) : 0;
                      return (
                        <div style={styles.analyticsItemCard} key={item.key}>
                          <div style={styles.analyticsItemHeader}>
                            <span style={{ ...styles.analyticsLabelBadge, color: item.color, backgroundColor: item.bg }}>
                              {item.key}
                            </span>
                            <span style={styles.analyticsPercentText}>{percent}%</span>
                          </div>
                          <div style={styles.analyticsItemValue}>{item.val}</div>
                          <div style={styles.analyticsItemLabel}>{item.label}</div>
                          <div style={styles.analyticsProgressBarBg}>
                            <div style={{ ...styles.analyticsProgressBarFill, width: `${percent}%`, backgroundColor: item.color }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        );

      case 'employees': {
        // Filter employees based on the search query
        const filteredEmployees = metrics.activeEmployees.filter(emp => {
          const query = searchQuery.trim().toLowerCase();
          if (!query) return true;
          return (
            (emp.name && emp.name.toLowerCase().includes(query)) ||
            (emp.email && emp.email.toLowerCase().includes(query)) ||
            (emp.phone && emp.phone.toLowerCase().includes(query)) ||
            (emp.project_name && emp.project_name.toLowerCase().includes(query))
          );
        });

        // Group filtered employees by project name
        const employeesByProject = filteredEmployees.reduce((groups, emp) => {
          const projName = emp.project_name || 'General Pool';
          if (!groups[projName]) groups[projName] = [];
          groups[projName].push(emp);
          return groups;
        }, {});

        const projectNames = Object.keys(employeesByProject).sort();

        return (
          <div style={styles.tabContainer}>
            <div style={styles.sectionHeaderWithAction}>
              <div>
                <h3 style={styles.tableTitle}>Active Employees by Project</h3>
                <p style={styles.sectionSubtitle}>Roster of team members mapped to their respective project assignments.</p>
              </div>
              {metrics.activeEmployees.length > 0 && (
                <button style={styles.btnExport} onClick={exportRosterToCSV}>
                  📥 Export Roster (.csv)
                </button>
              )}
            </div>

            {/* Premium Search Filter Row */}
            {metrics.activeEmployees.length > 0 && (
              <div style={styles.filterRow}>
                <div style={styles.searchContainer}>
                  <svg style={styles.searchIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by employee name, email, phone, or project..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={styles.searchInput}
                  />
                  {searchQuery && (
                    <button style={styles.searchClear} onClick={() => setSearchQuery('')}>
                      ✕
                    </button>
                  )}
                </div>
              </div>
            )}
            
            {metrics.activeEmployees.length === 0 ? (
              <EmptyState
                icon="👥"
                title="No Active Employees in Business Unit"
                description="There are currently no staff members onboarded into projects under your Business Unit."
                actionLabel="Import Team Data"
                onAction={() => handleTabChange('onboard')}
              />
            ) : filteredEmployees.length === 0 ? (
              <EmptyState
                icon="🔍"
                title="No Team Members Found"
                description={`No staff members match "${searchQuery}". Try a different project or name search.`}
                actionLabel="Clear Search"
                onAction={() => setSearchQuery('')}
              />
            ) : (
              projectNames.map((projName) => {
                const list = employeesByProject[projName];
                const matchedProj = metrics.projectHeadcounts.find(p => p.project_name === projName);
                return (
                  <div style={styles.projectAccordionCard} key={projName}>
                    <div style={styles.projectAccordionHeader}>
                      <div style={styles.projectAccordionTitleGroup}>
                        <span style={styles.projectFolderIcon}>📁</span>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <h4 style={styles.projectAccordionName}>{projName}</h4>
                            {matchedProj && matchedProj.manager_name && (
                              <button
                                style={styles.staffManagerPill}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedManager(matchedProj);
                                }}
                                title="Click to view manager details"
                              >
                                👤 Lead: <strong>{matchedProj.manager_name.trim()}</strong>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      <span style={styles.projectCountBadge}>{list.length} Members</span>
                    </div>
                    <div style={styles.projectAccordionContent}>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={styles.table}>
                          <thead>
                            <tr style={styles.trHeader}>
                              <th style={styles.th}>Employee Name</th>
                              <th style={styles.th}>Email Address</th>
                              <th style={styles.th}>Phone</th>
                              <th style={styles.th}>Interviewer Level</th>
                              <th style={styles.th}>Joining Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {list.map((emp, index) => (
                              <tr key={index} style={styles.tr}>
                                <td style={styles.tdTitle}>{emp.name}</td>
                                <td style={styles.td}>{emp.email}</td>
                                <td style={styles.td}>{emp.phone || 'N/A'}</td>
                                <td style={styles.td}>
                                  <span style={{
                                    padding: '3px 8px',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    background: emp.interviewer_level === 'NONE' ? 'rgba(100, 116, 139, 0.1)' : 'rgba(79, 70, 229, 0.1)',
                                    color: emp.interviewer_level === 'NONE' ? '#64748b' : 'var(--accent-primary, #4f46e5)'
                                  }}>
                                    {emp.interviewer_level === 'NONE' ? 'Non-Interviewer' : emp.interviewer_level}
                                  </span>
                                </td>
                                <td style={styles.td}>
                                  {emp.joining_date ? new Date(emp.joining_date).toLocaleDateString() : 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        );
      }

      case 'leavers':
        return (
          <div style={styles.tabContainer}>
            <div style={styles.sectionHeader}>
              <h3 style={styles.tableTitle}>Exit Approvals & Leavers Directory</h3>
              <p style={styles.sectionSubtitle}>Sign off on final clearances and review offboarded personnel across your Business Unit.</p>
            </div>

            {/* Top Card: Pending Exit Clearances */}
            <BUExitApprovals onAction={fetchBUMetrics} />

            {/* Bottom Card: Historical Exits & Leavers Directory */}
            <div style={styles.tableCard}>
              <div style={styles.cardHeaderBlock}>
                <h4 style={styles.tableTitle}>Historical Exits & Offboarded Leavers</h4>
                <p style={styles.sectionSubtitle}>Complete log of team members who have exited or offboarded.</p>
              </div>
              {metrics.leaverEmployees.length === 0 ? (
                <div style={styles.empty}>No employee exits recorded.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={styles.table}>
                    <thead>
                      <tr style={styles.trHeader}>
                        <th style={styles.th}>Employee Name</th>
                        <th style={styles.th}>Project</th>
                        <th style={styles.th}>Timeline</th>
                        <th style={styles.th}>Status</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metrics.leaverEmployees.map((emp, index) => (
                        <tr key={index} style={styles.tr}>
                          <td style={styles.tdTitle}>
                            <div>
                              <div style={{ fontWeight: '600' }}>{emp.name}</div>
                              <div style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)' }}>{emp.email}</div>
                            </div>
                          </td>
                          <td style={styles.td}>{emp.project_name || 'General Pool'}</td>
                          <td style={styles.td}>
                            <div style={{ fontSize: '12px' }}>
                              <div><span style={{ color: 'var(--text-muted)' }}>Join:</span> {new Date(emp.joining_date).toLocaleDateString()}</div>
                              {emp.last_working_day && (
                                <div><span style={{ color: '#ef4444' }}>Exit:</span> {new Date(emp.last_working_day).toLocaleDateString()}</div>
                              )}
                            </div>
                          </td>
                          <td style={styles.td}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: '700',
                              background: emp.user_deleted_at ? 'rgba(100, 116, 139, 0.1)' :
                                          emp.exit_status === 'EXIT_APPROVED' ? 'rgba(22, 163, 74, 0.1)' : 
                                          'rgba(245, 158, 11, 0.1)',
                              color: emp.user_deleted_at ? '#64748b' :
                                     emp.exit_status === 'EXIT_APPROVED' ? '#16a34a' : 
                                     '#f59e0b'
                            }}>
                              {emp.user_deleted_at ? 'OFFBOARDED' :
                               emp.exit_status === 'EXIT_APPROVED' ? 'APPROVED' : 
                               'PENDING'}
                            </span>
                          </td>
                          <td style={{ ...styles.td, textAlign: 'right', padding: '16px' }}>
                            <button
                              style={styles.btnNotifySmall}
                              onClick={() => handleNotify(emp.id)}
                              disabled={notifyingId === emp.id}
                            >
                              {notifyingId === emp.id ? 'Sending...' : 'Notify'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );

      case 'requisitions':
        return <BURequisitionApprovals onAction={fetchBUMetrics} />;

      case 'interviews': {
        // Cross-reference active employee levels
        const employeesByName = metrics.activeEmployees.reduce((acc, emp) => {
          acc[emp.name] = emp;
          return acc;
        }, {});

        // Compute scheduled/completed/total loads
        const interviewerLoads = metrics.scheduledInterviewsList.reduce((acc, interview) => {
          const name = interview.interviewer_name || 'Unassigned';
          if (!acc[name]) {
            acc[name] = {
              name,
              level: employeesByName[name]?.interviewer_level || 'L1',
              scheduledCount: 0,
              completedCount: 0,
              totalCount: 0
            };
          }
          if (interview.status === 'SCHEDULED') {
            acc[name].scheduledCount += 1;
          } else if (interview.status === 'COMPLETED') {
            acc[name].completedCount += 1;
          }
          acc[name].totalCount += 1;
          return acc;
        }, {});

        const sortedInterviewers = Object.values(interviewerLoads).sort((a, b) => b.scheduledCount - a.scheduledCount);

        return (
          <div style={styles.tabContainer}>
            <div style={styles.sectionHeader}>
              <h3 style={styles.tableTitle}>Interview & Load Management</h3>
              <p style={styles.sectionSubtitle}>Monitor scheduled screenings and balance workloads among interviewer panels.</p>
            </div>

            <div style={styles.interviewsGrid}>
              {/* Left Side: Scheduled Interviews Directory */}
              <div style={{ ...styles.tableCard, flex: 2, minWidth: '300px' }}>
                <h4 style={styles.tableSubTitle}>Scheduled Interviews Directory</h4>
                {metrics.scheduledInterviewsList.length === 0 ? (
                  <div style={styles.empty}>No scheduled interviews found.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                      <thead>
                        <tr style={styles.trHeader}>
                          <th style={styles.th}>Candidate</th>
                          <th style={styles.th}>Job Opening</th>
                          <th style={styles.th}>Experience</th>
                          <th style={styles.th}>Project</th>
                          <th style={styles.th}>Round</th>
                          <th style={styles.th}>Interviewer</th>
                          <th style={styles.th}>Scheduled Date</th>
                          <th style={styles.th}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {metrics.scheduledInterviewsList.map((int, index) => (
                          <tr key={index} style={styles.tr}>
                            <td style={styles.tdTitle}>
                              <div>
                                <div style={{ fontWeight: '600' }}>{int.candidate_name || 'Candidate'}</div>
                                <div style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)' }}>{int.candidate_email || 'N/A'}</div>
                              </div>
                            </td>
                            <td style={styles.td}>{int.job_title || 'N/A'}</td>
                            <td style={styles.td}>{int.exp_required || 'N/A'}</td>
                            <td style={styles.td}>{int.project_name || 'General Pool'}</td>
                            <td style={styles.td}>
                              <span style={{
                                padding: '3px 8px',
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: '700',
                                background: 'rgba(59, 130, 246, 0.1)',
                                color: '#3b82f6'
                              }}>
                                {int.round_type}
                              </span>
                            </td>
                            <td style={styles.td}>{int.interviewer_name || 'Assigned Interviewer'}</td>
                            <td style={styles.td}>{new Date(int.scheduled_date).toLocaleString()}</td>
                            <td style={styles.td}>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: '700',
                                background: int.status === 'COMPLETED' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                                color: int.status === 'COMPLETED' ? 'var(--success-color, #10b981)' : '#3b82f6'
                              }}>
                                {int.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Right Side: Interviewer Load Balance Panel */}
              <div style={{ ...styles.tableCard, flex: 1, minWidth: '280px' }}>
                <h4 style={styles.tableSubTitle}>Interviewer Load Balancing</h4>
                <p style={styles.loadSummaryText}>Review scheduled interview counts per assessor to prevent team fatigue.</p>
                
                {sortedInterviewers.length === 0 ? (
                  <div style={styles.empty}>No interviewer allocations found.</div>
                ) : (
                  <div style={styles.loadList}>
                    {sortedInterviewers.map((inv) => {
                      const isHighLoad = inv.scheduledCount >= 3;
                      return (
                        <div style={styles.loadItemCard} key={inv.name}>
                          <div style={styles.loadItemHeader}>
                            <div>
                              <div style={styles.loadItemName}>{inv.name}</div>
                              <span style={{
                                fontSize: '10px',
                                fontWeight: '800',
                                color: inv.level === 'NONE' ? '#64748b' : 'var(--accent-primary, #4f46e5)',
                                backgroundColor: inv.level === 'NONE' ? 'rgba(100, 116, 139, 0.1)' : 'rgba(79, 70, 229, 0.1)',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                display: 'inline-block',
                                marginTop: '4px'
                              }}>
                                {inv.level === 'NONE' ? 'L1 Assessor' : inv.level}
                              </span>
                            </div>
                            {isHighLoad ? (
                              <span style={styles.highLoadBadge}>🔥 HIGH LOAD</span>
                            ) : (
                              <span style={styles.normalLoadBadge}>✅ BALANCED</span>
                            )}
                          </div>

                          <div style={styles.loadStatsRow}>
                            <div style={styles.loadStatDetail}>
                              <span style={styles.loadStatLabel}>Scheduled</span>
                              <span style={styles.loadStatValBlue}>{inv.scheduledCount}</span>
                            </div>
                            <div style={styles.loadStatDetail}>
                              <span style={styles.loadStatLabel}>Completed</span>
                              <span style={styles.loadStatValGreen}>{inv.completedCount}</span>
                            </div>
                          </div>

                          <div style={styles.loadBarBg}>
                            <div style={{
                              ...styles.loadBarFill,
                              width: `${inv.totalCount > 0 ? (inv.scheduledCount / inv.totalCount) * 100 : 0}%`,
                              backgroundColor: isHighLoad ? '#ef4444' : '#3b82f6'
                            }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      }

      case 'positions':
        return (
          <div style={styles.tableCard}>
            <h3 style={styles.tableTitle}>Filled Positions Archive</h3>
            {metrics.filledPositionsList.length === 0 ? (
              <div style={styles.empty}>No filled positions recorded.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.trHeader}>
                      <th style={styles.th}>Job Title</th>
                      <th style={styles.th}>Project</th>
                      <th style={styles.th}>Required Skillset</th>
                      <th style={styles.th}>Experience</th>
                      <th style={styles.th}>Budget</th>
                      <th style={styles.th}>Hiring Manager</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.filledPositionsList.map((pos, index) => (
                      <tr key={index} style={styles.tr}>
                        <td style={styles.tdTitle}>{pos.job_title}</td>
                        <td style={styles.td}>{pos.project_name}</td>
                        <td style={styles.td}>{pos.skillset}</td>
                        <td style={styles.td}>{pos.exp_required}</td>
                        <td style={styles.td}>₹{pos.budget} LPA</td>
                        <td style={styles.td}>{pos.manager_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );

      case 'onboard':
        return <DataImport />;

      default:
        return null;
    }
  };

  return (
    <div style={styles.wrapper}>
      <Navbar 
        isLoggedIn={true} 
        userName={buUser?.full_name} 
        activeTab={activeTab} 
        onTabChange={handleTabChange}
      />
      <div style={styles.dashboardContainer}>
        {/* MAIN PANEL */}
        <main style={styles.mainContent}>
          
          {/* GLOBAL STATS ROW */}
          {!loading && activeTab === 'dashboard' && (
            <div style={styles.statsRow}>
              {/* Total Employees */}
              <div style={styles.statCard}>
                <div style={styles.statIconContainerIndigo}>
                  <svg style={styles.statIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div style={styles.statInfo}>
                  <h3 style={styles.statLabel}>Total Active Employees</h3>
                  <p style={styles.statValue}>{metrics.totalEmployeeCount}</p>
                </div>
              </div>

              {/* Active Projects */}
              <div style={styles.statCard}>
                <div style={styles.statIconContainerPurple}>
                  <svg style={styles.statIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div style={styles.statInfo}>
                  <h3 style={styles.statLabel}>Active Projects</h3>
                  <p style={styles.statValue}>{metrics.projectHeadcounts.length}</p>
                </div>
              </div>

              {/* Interviews Scheduled */}
              <div style={styles.statCard}>
                <div style={styles.statIconContainerBlue}>
                  <svg style={styles.statIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div style={styles.statInfo}>
                  <h3 style={styles.statLabel}>Interviews Scheduled</h3>
                  <p style={styles.statValue}>{metrics.scheduledInterviewsCount}</p>
                </div>
              </div>

              {/* Positions Filled */}
              <div style={styles.statCard}>
                <div style={styles.statIconContainerGreen}>
                  <svg style={styles.statIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <div style={styles.statInfo}>
                  <h3 style={styles.statLabel}>Positions Filled</h3>
                  <p style={styles.statValue}>{metrics.filledPositionsCount}</p>
                </div>
              </div>
            </div>
          )}

          {/* TAB CONTENT PANEL */}
          <div style={styles.tabContentBlock}>
            {renderContent()}
          </div>
        </main>
      </div>

      {/* MANAGER DETAILS MODAL */}
      {selectedManager && (
        <div 
          style={styles.modalOverlay}
          onClick={() => setSelectedManager(null)}
        >
          <div 
            style={styles.modalBox}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-manager-title"
          >
            {/* Modal Header */}
            <div style={styles.modalHeader}>
              <div style={styles.modalHeaderLeft}>
                <div style={styles.modalAvatarCircle}>
                  {selectedManager.manager_name ? selectedManager.manager_name.trim().charAt(0).toUpperCase() : 'M'}
                </div>
                <div>
                  <h3 id="modal-manager-title" style={styles.modalManagerName}>
                    {selectedManager.manager_name ? selectedManager.manager_name.trim() : 'Unassigned Manager'}
                  </h3>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' }}>
                    <span style={styles.modalRoleBadge}>PROJECT MANAGER</span>
                    <span style={styles.modalActiveBadge}>● ACTIVE</span>
                  </div>
                </div>
              </div>
              <button 
                style={styles.modalCloseIconBtn} 
                onClick={() => setSelectedManager(null)}
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div style={styles.modalBody}>
              {/* Contact Information Card */}
              <div style={styles.modalSectionCard}>
                <h4 style={styles.modalSectionHeading}>
                  <span>👤</span> Manager Information & Contact
                </h4>
                <div style={styles.modalInfoGrid}>
                  <div style={styles.modalInfoItem}>
                    <span style={styles.modalInfoLabel}>Full Name</span>
                    <span style={styles.modalInfoText}>{selectedManager.manager_name ? selectedManager.manager_name.trim() : 'N/A'}</span>
                  </div>
                  <div style={styles.modalInfoItem}>
                    <span style={styles.modalInfoLabel}>Official Email</span>
                    {selectedManager.manager_email ? (
                      <a 
                        href={`mailto:${selectedManager.manager_email}`} 
                        style={styles.modalLinkEmail}
                      >
                        {selectedManager.manager_email}
                      </a>
                    ) : (
                      <span style={styles.modalInfoText}>Not provided</span>
                    )}
                  </div>
                  <div style={styles.modalInfoItem}>
                    <span style={styles.modalInfoLabel}>Phone Number</span>
                    {selectedManager.manager_phone ? (
                      <a 
                        href={`tel:${selectedManager.manager_phone}`} 
                        style={styles.modalLinkPhone}
                      >
                        {selectedManager.manager_phone}
                      </a>
                    ) : (
                      <span style={styles.modalInfoText}>Not available</span>
                    )}
                  </div>
                  <div style={styles.modalInfoItem}>
                    <span style={styles.modalInfoLabel}>Leadership Tier</span>
                    <span style={styles.modalInfoText}>Project Lead (L2 Management)</span>
                  </div>
                </div>
              </div>

              {/* Assigned Project Section */}
              <div style={styles.modalSectionCard}>
                <h4 style={styles.modalSectionHeading}>
                  <span>🚀</span> Assigned Project Allocation
                </h4>
                <div style={styles.modalProjectHighlight}>
                  <div style={styles.modalProjectTopRow}>
                    <span style={styles.modalProjectTitle}>📁 {selectedManager.project_name}</span>
                    <span style={styles.modalTeamCountPill}>
                      👥 {selectedManager.employee_count} Active Members
                    </span>
                  </div>
                  {selectedManager.project_description && (
                    <p style={styles.modalProjectDescText}>
                      {selectedManager.project_description}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={styles.modalFooter}>
              <button 
                style={styles.modalDismissBtn} 
                onClick={() => setSelectedManager(null)}
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrapper: {
    minHeight: '100vh',
    backgroundColor: 'var(--bg-page, #f8fafc)',
    color: 'var(--text-main, #0f172a)',
    display: 'flex',
    flexDirection: 'column'
  },
  dashboardContainer: {
    maxWidth: '1440px',
    margin: '0 auto',
    width: '100%',
    padding: '32px 24px 48px 24px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '28px'
  },
  mainContent: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '28px',
    minWidth: 0
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '20px',
    width: '100%',
    boxSizing: 'border-box'
  },
  statCard: {
    flex: '1 1 200px',
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
  },
  statIconContainerIndigo: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    backgroundColor: 'rgba(79, 70, 229, 0.1)',
    color: '#4f46e5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  statIconContainerBlue: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    color: '#3b82f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  statIconContainerGreen: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    color: '#10b981',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  statIconContainerPurple: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    backgroundColor: 'rgba(139, 92, 246, 0.1)',
    color: '#8b5cf6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  statIcon: {
    width: '24px',
    height: '24px'
  },
  statInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  statLabel: {
    margin: 0,
    fontSize: '12px',
    fontWeight: '500',
    color: 'var(--text-muted, #64748b)'
  },
  statValue: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '800'
  },
  tabContentBlock: {
    width: '100%'
  },
  tabContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  employeesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
    gap: '24px',
    alignItems: 'start'
  },
  tableCard: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
  },
  tableTitle: {
    margin: '0 0 20px 0',
    fontSize: '18px',
    fontWeight: '600'
  },
  empty: {
    textAlign: 'center',
    padding: '40px',
    color: 'var(--text-muted, #64748b)',
    fontSize: '14px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left'
  },
  trHeader: {
    borderBottom: '2px solid var(--border-color, #e2e8f0)'
  },
  th: {
    padding: '12px 16px',
    color: 'var(--text-muted, #64748b)',
    fontWeight: '600',
    fontSize: '13px'
  },
  tr: {
    borderBottom: '1px solid var(--border-color, #e2e8f0)'
  },
  tdTitle: {
    padding: '16px',
    fontWeight: '600'
  },
  td: {
    padding: '16px',
    fontSize: '14px'
  },
  progressRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    maxWidth: '240px'
  },
  percentageText: {
    fontSize: '13px',
    fontWeight: '600',
    width: '44px'
  },
  progressBarBg: {
    width: '100%',
    height: '8px',
    borderRadius: '99px',
    backgroundColor: '#e2e8f0',
    overflow: 'hidden'
  },
  progressBarFill: {
    height: '100%',
    borderRadius: '99px',
    transition: 'width 0.4s ease-out'
  },
  loader: {
    textAlign: 'center',
    padding: '48px',
    color: 'var(--text-muted, #64748b)',
    fontSize: '16px'
  },
  dashboardView: {
    display: 'flex',
    flexDirection: 'column',
    gap: '28px',
    width: '100%'
  },
  sectionHeader: {
    marginBottom: '16px'
  },
  sectionSubtitle: {
    margin: '4px 0 0 0',
    fontSize: '13px',
    color: 'var(--text-muted, #64748b)'
  },
  projectGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px',
    width: '100%'
  },
  projectCard: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '22px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.2s ease',
    cursor: 'default'
  },
  projectCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  projectBadge: {
    fontSize: '10px',
    fontWeight: '700',
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    color: '#10b981',
    padding: '2px 8px',
    borderRadius: '99px'
  },
  projectShareBadge: {
    fontSize: '11px',
    fontWeight: '600',
    color: 'var(--accent-primary, #4f46e5)'
  },
  projectCardName: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--text-main, #0f172a)'
  },
  projectCardMetrics: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: 'var(--text-muted, #64748b)',
    fontSize: '13px'
  },
  projectCardIcon: {
    width: '16px',
    height: '16px'
  },
  projectHeadcountText: {
    fontWeight: '500'
  },
  progressContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginTop: '6px'
  },
  progressLabel: {
    fontSize: '11px',
    fontWeight: '500',
    color: 'var(--text-muted, #64748b)'
  },
  projectAccordionCard: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
    marginBottom: '20px'
  },
  projectAccordionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid var(--border-color, #e2e8f0)',
    paddingBottom: '12px'
  },
  projectAccordionTitleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  projectFolderIcon: {
    fontSize: '20px'
  },
  projectAccordionName: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '700',
    color: 'var(--text-main, #0f172a)'
  },
  projectCountBadge: {
    fontSize: '11px',
    fontWeight: '700',
    backgroundColor: 'rgba(79, 70, 229, 0.1)',
    color: 'var(--accent-primary, #4f46e5)',
    padding: '4px 10px',
    borderRadius: '99px'
  },
  projectAccordionContent: {
    width: '100%'
  },
  btnNotifySmall: {
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(59, 130, 246, 0.15)',
    transition: 'all 0.2s ease'
  },
  filterRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '20px',
    width: '100%',
    boxSizing: 'border-box'
  },
  searchContainer: {
    position: 'relative',
    flex: 1,
    display: 'flex',
    alignItems: 'center'
  },
  searchIcon: {
    position: 'absolute',
    left: '14px',
    width: '18px',
    height: '18px',
    color: '#94a3b8',
    pointerEvents: 'none'
  },
  searchInput: {
    width: '100%',
    padding: '12px 16px 12px 42px',
    fontSize: '14px',
    borderRadius: '10px',
    border: '1px solid var(--border-color, #e2e8f0)',
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    color: 'var(--text-main, #0f172a)',
    outline: 'none',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
  },
  searchClear: {
    position: 'absolute',
    right: '14px',
    background: 'none',
    border: 'none',
    fontSize: '14px',
    color: '#94a3b8',
    cursor: 'pointer',
    padding: '4px'
  },
  analyticsCard: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
    boxSizing: 'border-box',
    width: '100%'
  },
  analyticsCardHeaderBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  pendingReviewPillBtn: {
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    color: '#d97706',
    border: '1px solid rgba(245, 158, 11, 0.25)',
    padding: '8px 16px',
    borderRadius: '99px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.2s ease'
  },
  analyticsTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--text-main, #0f172a)'
  },
  analyticsSubtitle: {
    margin: 0,
    fontSize: '13px',
    color: 'var(--text-muted, #64748b)'
  },
  analyticsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    width: '100%'
  },
  analyticsItemCard: {
    backgroundColor: 'var(--bg-surface, #ffffff)',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '12px',
    padding: '18px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    boxSizing: 'border-box',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease'
  },
  analyticsItemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  analyticsLabelBadge: {
    fontSize: '10px',
    fontWeight: '800',
    padding: '4px 8px',
    borderRadius: '6px',
    letterSpacing: '0.05em'
  },
  analyticsPercentText: {
    fontSize: '12px',
    fontWeight: '700',
    color: 'var(--text-muted, #64748b)'
  },
  analyticsItemValue: {
    fontSize: '28px',
    fontWeight: '800',
    color: 'var(--text-main, #0f172a)',
    lineHeight: 1
  },
  analyticsItemLabel: {
    fontSize: '12px',
    color: 'var(--text-muted, #64748b)',
    fontWeight: '500'
  },
  analyticsProgressBarBg: {
    height: '6px',
    width: '100%',
    backgroundColor: '#f1f5f9',
    borderRadius: '99px',
    overflow: 'hidden',
    marginTop: '4px'
  },
  analyticsProgressBarFill: {
    height: '100%',
    borderRadius: '99px',
    transition: 'width 0.4s ease-out'
  },
  sectionHeaderWithAction: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    width: '100%',
    boxSizing: 'border-box'
  },
  btnExport: {
    backgroundColor: 'var(--accent-primary, #4f46e5)',
    color: '#ffffff',
    border: 'none',
    padding: '10px 18px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.2)',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  interviewsGrid: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    width: '100%',
    boxSizing: 'border-box'
  },
  tableSubTitle: {
    margin: '0 0 12px 0',
    fontSize: '16px',
    fontWeight: '700',
    color: 'var(--text-main, #0f172a)'
  },
  loadSummaryText: {
    margin: '0 0 20px 0',
    fontSize: '13px',
    color: 'var(--text-muted, #64748b)',
    lineHeight: 1.4
  },
  loadList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  loadItemCard: {
    backgroundColor: 'var(--bg-surface, #ffffff)',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '12px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    boxSizing: 'border-box'
  },
  loadItemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  loadItemName: {
    fontSize: '14px',
    fontWeight: '700',
    color: 'var(--text-main, #0f172a)'
  },
  highLoadBadge: {
    fontSize: '10px',
    fontWeight: '800',
    color: '#ef4444',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    padding: '3px 8px',
    borderRadius: '6px',
    letterSpacing: '0.02em'
  },
  normalLoadBadge: {
    fontSize: '10px',
    fontWeight: '800',
    color: '#10b981',
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    padding: '3px 8px',
    borderRadius: '6px',
    letterSpacing: '0.02em'
  },
  loadStatsRow: {
    display: 'flex',
    gap: '16px'
  },
  loadStatDetail: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  loadStatLabel: {
    fontSize: '11px',
    color: 'var(--text-muted, #64748b)',
    fontWeight: '500'
  },
  loadStatValBlue: {
    fontSize: '16px',
    fontWeight: '800',
    color: '#3b82f6'
  },
  loadStatValGreen: {
    fontSize: '16px',
    fontWeight: '800',
    color: '#10b981'
  },
  loadBarBg: {
    height: '6px',
    width: '100%',
    backgroundColor: '#f1f5f9',
    borderRadius: '99px',
    overflow: 'hidden'
  },
  loadBarFill: {
    height: '100%',
    borderRadius: '99px',
    transition: 'width 0.4s ease-out'
  },
  // Manager Card Pill on Project Card
  managerCardPill: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    backgroundColor: 'rgba(79, 70, 229, 0.05)',
    border: '1px solid rgba(79, 70, 229, 0.15)',
    borderRadius: '12px',
    padding: '8px 12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    userSelect: 'none'
  },
  managerAvatarChip: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: 'var(--accent-primary, #4f46e5)',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  managerInfoCol: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: 1
  },
  managerLabelText: {
    fontSize: '10px',
    color: 'var(--text-muted, #64748b)',
    fontWeight: '500',
    lineHeight: '1.2'
  },
  managerNameText: {
    fontSize: '13px',
    fontWeight: '700',
    color: 'var(--accent-primary, #4f46e5)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  managerViewBtn: {
    fontSize: '11px',
    fontWeight: '700',
    color: 'var(--accent-primary, #4f46e5)',
    padding: '3px 8px',
    borderRadius: '6px',
    backgroundColor: 'rgba(79, 70, 229, 0.08)',
    whiteSpace: 'nowrap'
  },



  // Staff Accordion Manager Pill
  staffManagerPill: {
    background: 'rgba(79, 70, 229, 0.08)',
    border: '1px solid rgba(79, 70, 229, 0.2)',
    color: 'var(--accent-primary, #4f46e5)',
    borderRadius: '8px',
    padding: '3px 8px',
    fontSize: '11px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    transition: 'all 0.2s'
  },

  // Modal Styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
    backdropFilter: 'blur(8px)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  },
  modalBox: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.98))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '20px',
    width: '100%',
    maxWidth: '520px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid var(--border-color, #e2e8f0)',
    backgroundColor: 'rgba(248, 250, 252, 0.6)'
  },
  modalHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px'
  },
  modalAvatarCircle: {
    width: '46px',
    height: '46px',
    borderRadius: '12px',
    backgroundColor: 'var(--accent-primary, #4f46e5)',
    color: '#ffffff',
    fontSize: '18px',
    fontWeight: '800',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 10px rgba(79, 70, 229, 0.25)'
  },
  modalManagerName: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--text-main, #0f172a)'
  },
  modalRoleBadge: {
    fontSize: '10px',
    fontWeight: '800',
    backgroundColor: 'rgba(79, 70, 229, 0.1)',
    color: 'var(--accent-primary, #4f46e5)',
    padding: '2px 8px',
    borderRadius: '6px'
  },
  modalActiveBadge: {
    fontSize: '10px',
    fontWeight: '700',
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    color: '#10b981',
    padding: '2px 8px',
    borderRadius: '6px'
  },
  modalCloseIconBtn: {
    background: 'none',
    border: 'none',
    fontSize: '18px',
    color: 'var(--text-muted, #64748b)',
    cursor: 'pointer',
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s'
  },
  modalBody: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
    maxHeight: '70vh',
    overflowY: 'auto'
  },
  modalSectionCard: {
    backgroundColor: 'rgba(248, 250, 252, 0.5)',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '14px',
    padding: '16px'
  },
  modalSectionHeading: {
    margin: '0 0 14px 0',
    fontSize: '13px',
    fontWeight: '700',
    color: 'var(--text-main, #0f172a)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  modalInfoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '14px'
  },
  modalInfoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px'
  },
  modalInfoLabel: {
    fontSize: '11px',
    color: 'var(--text-muted, #64748b)',
    fontWeight: '500'
  },
  modalInfoText: {
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--text-main, #0f172a)',
    wordBreak: 'break-all'
  },
  modalLinkEmail: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#4f46e5',
    textDecoration: 'none',
    wordBreak: 'break-all'
  },
  modalLinkPhone: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#10b981',
    textDecoration: 'none'
  },
  modalProjectHighlight: {
    backgroundColor: 'var(--bg-surface, #ffffff)',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '12px',
    padding: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  modalProjectTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px'
  },
  modalProjectTitle: {
    fontSize: '15px',
    fontWeight: '700',
    color: 'var(--text-main, #0f172a)'
  },
  modalTeamCountPill: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#4f46e5',
    backgroundColor: 'rgba(79, 70, 229, 0.08)',
    padding: '3px 10px',
    borderRadius: '99px'
  },
  modalProjectDescText: {
    margin: 0,
    fontSize: '12px',
    color: 'var(--text-muted, #64748b)',
    lineHeight: '1.5'
  },
  modalFooter: {
    padding: '16px 24px',
    borderTop: '1px solid var(--border-color, #e2e8f0)',
    display: 'flex',
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(248, 250, 252, 0.4)'
  },
  modalDismissBtn: {
    backgroundColor: 'var(--accent-primary, #4f46e5)',
    color: '#ffffff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.2)',
    transition: 'opacity 0.2s'
  }
};
